import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../../../../components/common/GradientCircularProgress';
import TableCard, { TableColumn } from '../../../../../components/common/TableCard';
import { useThemeContext } from '../../../../../contexts/BaseThemeContextProvider';
import useBackendApi from '../../../../../hooks/useBackendApi';
import { AccountLocation, AccountType } from '../../../../../types/account';
import { RequestOptions } from '../../../../../types/httpRequests';
import PrimaryBlueButton from '../../../../../components/common/buttons/PrimaryBlueButton';
import LocationDrawer, { AccountLocationDrawerMode, LocationDrawerPayload } from './LocationDrawer';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
};

type ActionMessage = {
  type: 'success' | 'error';
  text: string;
};

export type AccountLocationsResponse = {
  message?: string;
  status_code?: number;
  detail?: string;
  account?: AccountType;
  locations?: AccountLocation[];
  user_org_roles?: string[];
  user_acc_roles?: string[];
};

type ApiFetchRequest = (options: RequestOptions) => Promise<{
  status: number;
  body?: unknown;
  isError?: boolean;
} | null>;

export type FetchAccountLocationsResult = {
  status: number;
  body?: AccountLocationsResponse;
  isError: boolean;
  unauthorized: boolean;
  notFound: boolean;
};

export const fetchAccountLocations = async ({
  apiFetchRequest,
  backendURL,
  orgPathName,
  accPathName,
}: {
  apiFetchRequest: ApiFetchRequest;
  backendURL: string;
  orgPathName: string;
  accPathName: string;
}): Promise<FetchAccountLocationsResult | null> => {
  const opts: RequestOptions = {
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(accPathName)}/locations/`,
    method: 'GET',
  };

  const res = await apiFetchRequest(opts);
  if (!res) {
    return null;
  }

  const body = res.body as AccountLocationsResponse | undefined;
  const unauthorized =
    res.status === 401 ||
    body?.status_code === 401 ||
    body?.message === 'UNAUTHENTICATED' ||
    body?.message === 'UNAUTHORIZED' ||
    body?.message === 'NO_ACCESS';
  const notFound = res.status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND';

  return {
    status: res.status,
    body,
    isError: Boolean(res.isError),
    unauthorized,
    notFound,
  };
};

export default function AccountLocations(): JSX.Element {
  const { org_path_name, acc_path_name } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const [account, setAccount] = useState<AccountType | null>(null);
  const [locations, setLocations] = useState<AccountLocation[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [userOrgRoles, setUserOrgRoles] = useState<string[]>([]);
  const [userAccRoles, setUserAccRoles] = useState<string[]>([]);

  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);

  // Actions menu state (per-location)
  const [menuAnchorEl, setMenuAnchorEl] = useState<Record<number, HTMLElement | null>>({});
  const [deletingLocationId, setDeletingLocationId] = useState<number | null>(null);

  // Drawer state for create/edit
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<AccountLocationDrawerMode>('create');
  const [drawerInitialLocation, setDrawerInitialLocation] = useState<AccountLocation | null>(null);
  const [isSubmittingLocation, setIsSubmittingLocation] = useState(false);

  const isOrgAdmin = userOrgRoles.some((r) => r?.toLowerCase() === 'admin');
  const isAccAdmin = userAccRoles.some((r) => r?.toLowerCase() === 'admin');
  const isAccMember = userAccRoles.some((r) => r?.toLowerCase() === 'member');

  const canAddLocations = isOrgAdmin || isAccAdmin || isAccMember;
  const canEditLocations = isOrgAdmin || isAccAdmin || isAccMember;
  const canDeleteLocations = false && (isOrgAdmin || isAccAdmin);

  const canManageAny = canEditLocations || canDeleteLocations;

  const fail = useCallback(() => setErrorText('Something went wrong. Please try again.'), []);

  const handleCommonAuthNav = useCallback(
    (resStatus?: number, body?: { status_code?: number; message?: string }) => {
      if (
        resStatus === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'UNAUTHORIZED' ||
        body?.message === 'NO_ACCESS'
      ) {
        navigate('/401.html');
        return true;
      }
      if (resStatus === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return true;
      }
      return false;
    },
    [navigate],
  );

  const fetchLocations = useCallback(async () => {
    if (!org_path_name || !acc_path_name) return;

    try {
      const res = await fetchAccountLocations({
        apiFetchRequest,
        backendURL,
        orgPathName: org_path_name,
        accPathName: acc_path_name,
      });
      if (!res) {
        fail();
        return;
      }

      if (handleCommonAuthNav(res.status, res.body)) return;

      if (res.isError || res.status !== 200 || res.body?.message !== 'SUCCESS' || !res.body?.account) {
        fail();
        return;
      }

      setAccount(res.body.account);
      setLocations(Array.isArray(res.body.locations) ? res.body.locations : []);
      setUserOrgRoles(Array.isArray(res.body.user_org_roles) ? res.body.user_org_roles : []);
      setUserAccRoles(Array.isArray(res.body.user_acc_roles) ? res.body.user_acc_roles : []);
      setErrorText(null);
    } catch {
      fail();
    }
  }, [acc_path_name, apiFetchRequest, fail, handleCommonAuthNav, org_path_name]);

  useEffect(() => {
    if (!org_path_name || !acc_path_name) {
      navigate('/404.html');
      return;
    }
    setActionMessage(null);
    void fetchLocations();
  }, [acc_path_name, fetchLocations, navigate, org_path_name]);

  const closeDrawer = useCallback(() => {
    if (isSubmittingLocation) return;
    setIsDrawerOpen(false);
    setDrawerMode('create');
    setDrawerInitialLocation(null);
  }, [isSubmittingLocation]);

  const openCreateDrawer = useCallback(() => {
    if (!canAddLocations) return;
    setActionMessage(null);
    setDrawerMode('create');
    setDrawerInitialLocation(null);
    setIsDrawerOpen(true);
  }, [canAddLocations]);

  const openEditDrawer = useCallback(
    (loc: AccountLocation) => {
      if (!canEditLocations) return;
      setActionMessage(null);
      setDrawerMode('edit');
      setDrawerInitialLocation(loc);
      setIsDrawerOpen(true);
    },
    [canEditLocations],
  );

  const handleOpenActionsMenu = useCallback((clickEvent: MouseEvent<HTMLElement>, locId: number) => {
    clickEvent.stopPropagation();
    const target = clickEvent.currentTarget as HTMLElement;
    setMenuAnchorEl((prev) => ({ ...prev, [locId]: target }));
  }, []);

  const handleCloseActionsMenu = useCallback((locId: number) => {
    setMenuAnchorEl((prev) => ({ ...prev, [locId]: null }));
  }, []);

  const isAnyMenuOpen = useMemo(() => Object.values(menuAnchorEl).some(Boolean), [menuAnchorEl]);

  const handleCreateLocation = useCallback(
    async (payload: LocationDrawerPayload) => {
      if (!org_path_name || !acc_path_name) return;
      if (!canAddLocations) return;

      setIsSubmittingLocation(true);
      setActionMessage(null);

      try {
        const res = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/locations/`,
          method: 'POST',
          body: payload,
        });

        if (!res) {
          setActionMessage({ type: 'error', text: 'Unable to create location.' });
          return;
        }

        const body = res.body as { message?: string; detail?: string; status_code?: number };
        if (handleCommonAuthNav(res.status, body)) return;

        if (res.isError || body?.message !== 'SUCCESS') {
          setActionMessage({ type: 'error', text: body?.detail || 'Unable to create location.' });
          return;
        }

        setActionMessage({ type: 'success', text: 'Location created successfully.' });
        await fetchLocations();
        closeDrawer();
      } catch {
        setActionMessage({ type: 'error', text: 'Unable to create location. Please try again.' });
      } finally {
        setIsSubmittingLocation(false);
      }
    },
    [acc_path_name, apiFetchRequest, canAddLocations, closeDrawer, fetchLocations, handleCommonAuthNav, org_path_name],
  );

  const handleUpdateLocation = useCallback(
    async (locId: number, payload: LocationDrawerPayload) => {
      if (!org_path_name || !acc_path_name) return;
      if (!canEditLocations) return;

      setIsSubmittingLocation(true);
      setActionMessage(null);

      try {
        const res = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
            acc_path_name,
          )}/locations/${encodeURIComponent(String(locId))}`,
          method: 'PUT',
          body: payload,
        });

        if (!res) {
          setActionMessage({ type: 'error', text: 'Unable to update location.' });
          return;
        }

        const body = res.body as { message?: string; detail?: string; status_code?: number };
        if (handleCommonAuthNav(res.status, body)) return;

        if (res.isError || (res.status !== 200 && res.status !== 204) || body?.message !== 'SUCCESS') {
          setActionMessage({ type: 'error', text: body?.detail || 'Unable to update location.' });
          return;
        }

        setActionMessage({ type: 'success', text: 'Location updated successfully.' });
        await fetchLocations();
        closeDrawer();
      } catch {
        setActionMessage({ type: 'error', text: 'Unable to update location. Please try again.' });
      } finally {
        setIsSubmittingLocation(false);
      }
    },
    [acc_path_name, apiFetchRequest, canEditLocations, closeDrawer, fetchLocations, handleCommonAuthNav, org_path_name],
  );

  const handleSubmitDrawer = useCallback(
    async (payload: LocationDrawerPayload) => {
      if (drawerMode === 'create') {
        await handleCreateLocation(payload);
        return;
      }

      const locId = drawerInitialLocation?.acc_location_id;
      if (!locId) {
        setActionMessage({ type: 'error', text: 'Unable to update location.' });
        return;
      }

      await handleUpdateLocation(locId, payload);
    },
    [drawerMode, handleCreateLocation, handleUpdateLocation, drawerInitialLocation],
  );

  const handleDeleteLocation = useCallback(
    async (loc: AccountLocation) => {
      if (!org_path_name || !acc_path_name) return;
      if (!canDeleteLocations) return;

      const locId = loc.acc_location_id;
      if (!locId) return;

      const confirmed = window.confirm('Are you sure you want to delete this location? This action cannot be undone.');
      if (!confirmed) {
        handleCloseActionsMenu(locId);
        return;
      }

      setDeletingLocationId(locId);
      setActionMessage(null);
      handleCloseActionsMenu(locId);

      try {
        const res = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
            acc_path_name,
          )}/locations/${encodeURIComponent(String(locId))}`,
          method: 'DELETE',
        });

        if (!res) {
          setActionMessage({ type: 'error', text: 'Unable to delete location.' });
          return;
        }

        const body = res.body as { message?: string; detail?: string; status_code?: number };
        if (handleCommonAuthNav(res.status, body)) return;

        if (res.isError || (res.status !== 200 && res.status !== 204) || body?.message !== 'SUCCESS') {
          setActionMessage({ type: 'error', text: body?.detail || 'Unable to delete location.' });
          return;
        }

        setActionMessage({ type: 'success', text: 'Location deleted successfully.' });
        await fetchLocations();
      } catch {
        setActionMessage({ type: 'error', text: 'Unable to delete location. Please try again.' });
      } finally {
        setDeletingLocationId(null);
      }
    },
    [
      acc_path_name,
      apiFetchRequest,
      canDeleteLocations,
      fetchLocations,
      handleCloseActionsMenu,
      handleCommonAuthNav,
      org_path_name,
    ],
  );

  const renderLocationActions = useCallback(
    (loc: AccountLocation) => {
      const locId = loc.acc_location_id;
      if (!locId) return null;
      if (!canManageAny) return null;

      return (
        <>
          <IconButton aria-label="Location actions" onClick={(e) => handleOpenActionsMenu(e, locId)} size="small">
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={menuAnchorEl[locId] ?? null}
            open={Boolean(menuAnchorEl[locId])}
            onClose={() => handleCloseActionsMenu(locId)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            MenuListProps={{
              onClick: (e) => e.stopPropagation(),
            }}
          >
            {canEditLocations ? (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseActionsMenu(locId);
                  openEditDrawer(loc);
                }}
              >
                Edit Location
              </MenuItem>
            ) : null}

            {canDeleteLocations ? (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDeleteLocation(loc);
                }}
                disabled={deletingLocationId === locId}
              >
                {deletingLocationId === locId ? 'Deleting...' : 'Delete Location'}
              </MenuItem>
            ) : null}
          </Menu>
        </>
      );
    },
    [
      canDeleteLocations,
      canEditLocations,
      canManageAny,
      deletingLocationId,
      handleCloseActionsMenu,
      handleDeleteLocation,
      handleOpenActionsMenu,
      menuAnchorEl,
      openEditDrawer,
    ],
  );

  const renderAddress = useCallback((loc: AccountLocation) => {
    const addressLine1 = (loc.address_line_1 ?? '').trim();
    const addressLine2 = (loc.address_line_2 ?? '').trim();
    const city = (loc.address_city ?? '').trim();
    const state = (loc.address_state ?? '').trim();
    const zip = (loc.address_zip ?? '').trim();

    const streetParts = [addressLine1, addressLine2].filter(Boolean);
    const streetText = streetParts.join(' ');

    const cityText = city ? `${city},` : '';
    const stateZipText = [state, zip].filter(Boolean).join(' ');
    const cityStateZipText = [cityText, stateZipText].filter(Boolean).join(' ').trim();

    if (!streetText && !cityStateZipText) return 'N/A';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {streetText ? <Typography variant="body2">{streetText}</Typography> : null}
        {cityStateZipText ? <Typography variant="body2">{cityStateZipText}</Typography> : null}
      </Box>
    );
  }, []);

  const columns = useMemo<TableColumn<AccountLocation>[]>(() => {
    const base: TableColumn<AccountLocation>[] = [
      {
        label: 'Location',
        render: (loc) => (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography>{loc.name}</Typography>
            {loc.description ? <Typography variant="body2">{loc.description}</Typography> : null}
          </Box>
        ),
      },
      {
        label: 'Address',
        render: (loc) => renderAddress(loc),
      },
    ];

    if (canManageAny) {
      base.push({
        label: 'Actions',
        render: (loc) => renderLocationActions(loc),
      });
    }

    return base;
  }, [canManageAny, renderAddress, renderLocationActions]);

  if ((isLoading && !account && !errorText) || !org_path_name || !acc_path_name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  if (!account) return <></>;

  const addLocationButton = canAddLocations ? (
    <PrimaryBlueButton
      onClick={openCreateDrawer}
      sxProps={{
        height: { md: 40, xs: 40, sm: 40 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: { xs: '100%', md: '160px' },
      }}
      buttonText="Add Location"
    />
  ) : null;

  return (
    <Box
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
        padding: '40px',
      }}
    >
      {actionMessage ? (
        <Alert severity={actionMessage.type} onClose={() => setActionMessage(null)} sx={{ mt: 2 }}>
          {actionMessage.text}
        </Alert>
      ) : null}

      <Box sx={{ mt: 0 }}>
        {locations.length ? (
          <TableCard
            title="Locations"
            rows={locations}
            columns={columns}
            getRowKey={(location) => location.acc_location_id ?? location.name}
            actionButtons={addLocationButton}
            onRowClick={(location) => {
              if (isAnyMenuOpen) return;

              navigate(
                `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
                  acc_path_name,
                )}/locations/${encodeURIComponent(String(location.acc_location_id))}`,
              );
            }}
          />
        ) : (
          <Box
            sx={{
              backgroundColor: theme.profile.backgroundColor,
              borderRadius: '16px',
              padding: '40px',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6">No locations added to this account.</Typography>
            {addLocationButton ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>{addLocationButton}</Box>
            ) : null}
          </Box>
        )}
      </Box>

      <LocationDrawer
        open={isDrawerOpen}
        mode={drawerMode}
        initialLocation={drawerInitialLocation}
        onClose={closeDrawer}
        onSubmit={handleSubmitDrawer}
        isSubmitting={isSubmittingLocation}
      />
    </Box>
  );
}
